import { Header } from "../../components/Header";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useState } from "react";

import authApi from "../../../../api/authApi";
import { consumePostLoginRedirect, persistSessionValue } from "../../../../utils/authStorage";
import { RegisterValues } from "../../../../api/types";
import { signupFormSchema } from "../../../../utils/inputSchema";
import { getUserFriendlyErrorMessage } from "../../../../utils/errorMessages";
import googleLogo from '../../../../assets/login/google.jpg'

function Signup() {
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const formik = useFormik<RegisterValues & { agreeToTerms: boolean }>({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      phone: "",
      agreeToTerms: false,
    },
    validationSchema: signupFormSchema,

    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      setError(null);
      setForbidden(false);
      try {
        const { agreeToTerms, ...registerPayload } = values;

        const response = await authApi.signupUser(registerPayload);
        const customToken = response.custom_token;
        sessionStorage.setItem("custom_token", customToken);

        // SUCCESS: Send Email OTP After Register Success
        await authApi.sendEmailOtp(values.email);

        resetForm();
        navigate("/emailverification");
      } catch (err: any) {
        const message = getUserFriendlyErrorMessage(err, "Signup failed. Please try again.");
        if (message.toLowerCase().includes("already")) {
          setForbidden(true); //User already exists, Email already registered, Account already created"
        } else {
          setError(message);
        }
      } finally {
        setLoading(false)
      }
    },
  });

  const handleGoogleLogin = async () => {
    try {
      // SUCCESS: Redirect to home
      await authApi.googleSignIn();
      persistSessionValue('just_logged_in', 'true');
      sessionStorage.removeItem('login_cashflow_refresh_started');
      window.location.replace(consumePostLoginRedirect() || "/home");
    } catch (error) {
      console.error("Google login failed:", error);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      <main className="flex flex-col items-center flex-1 bg-gray-50 p-5">
        <div className="w-full max-w-[600px] bg-white rounded-[20px] border border-gray-200 shadow-sm p-8 flex flex-col gap-8">
          <div className="w-[536px] flex flex-col gap-2">
            <h1 className="font-sans font-semibold text-[28px] leading-none tracking-normal text-center">
              Create your Finz Account
            </h1>
            <p className="font-sans font-medium text-base leading-normal tracking-normal text-center">
              AI-driven financial insights at your fingertips.
            </p>
          </div>

          <form onSubmit={formik.handleSubmit} className="w-full flex flex-col gap-8">
            {/* --- GROUP 1: INPUT FIELDS --- */}
            {/* Internal gap is gap-6 (24px) based on your previous data */}
            <div className="w-full flex flex-col gap-6">
              {/* First Name */}
              <div className="flex flex-col gap-3">
                <label htmlFor="first_name" className="text-base font-semibold text-gray-700 leading-5">First name</label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  className="input w-full h-12 px-4 rounded-md border bg-gray-100 text-gray-800 text-sm font-normal leading-[150%] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.first_name && formik.errors.first_name && (
                  <span className="text-sm text-red-600">{formik.errors.first_name}</span>
                )}
              </div>

              {/* Last Name */}
              <div className="flex flex-col gap-3">
                <label htmlFor="last_name" className="text-base font-semibold text-gray-700 leading-5">Last name</label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  className="input w-full h-12 px-4 rounded-md border bg-gray-100 text-gray-800 text-sm font-normal leading-[150%] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.last_name && formik.errors.last_name && (
                  <span className="text-sm text-red-600">{formik.errors.last_name}</span>
                )}
              </div>

              {/* Email */}
              <div className="flex flex-col gap-3">
                <label htmlFor="email" className="text-base font-semibold text-gray-700 leading-5">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="input w-full h-12 px-4 rounded-md border bg-gray-100 text-gray-800 text-sm font-normal leading-[150%] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formik.values.email}
                  onChange={(e) => {
                    formik.handleChange(e);
                    setForbidden(false);
                  }}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email && (
                  <span className="text-sm text-red-600">{formik.errors.email}</span>
                )}
              </div>

              {/* Phone */}
              <div className="flex flex-col gap-3">
                <label htmlFor="phone" className="text-base font-semibold text-gray-700 leading-5">Phone</label>
                <div className="relative">
                  <div className="absolute left-0 top-0 h-full flex items-center pl-3 pointer-events-none">
                    <div className="flex items-center gap-2 h-6">
                      <img
                        src="/american_flag.png"
                        alt="US Flag"
                        className="w-5 h-auto object-contain select-none"
                      />
                      <span className="font-medium text-sm" style={{ color: '#989DA3' }}>+1</span>
                    </div>
                  </div>
                  <input
                    id="phone"
                    name="phone"
                    type="text"
                    className="input w-full h-12 px-4 pl-[72px] rounded-md border bg-gray-100 text-gray-800 text-sm font-normal leading-[150%] focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-[#989DA3]"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="(555) 000-0000"
                  />
                </div>
                {formik.touched.phone && formik.errors.phone && (
                  <span className="text-sm text-red-600">{formik.errors.phone}</span>
                )}
              </div>

              {/* Password */}
              <div className="flex flex-col gap-3">
                <label htmlFor="password" className="text-base font-semibold text-gray-700 leading-5">Password</label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    className="input w-full h-12 px-4 pr-10 rounded-md border bg-gray-100 text-gray-800 text-sm font-normal leading-[150%] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {/* 👇 The Eye Button */}
                  <button
                    type="button" // Important! Prevents the form from submitting when clicked
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {!showPassword ? (
                      // Icon: Eye Off (Hide)
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      // Icon: Eye On (Show)
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <span className="text-sm text-red-600">{formik.errors.password}</span>
                )}
              </div>
            </div>

            {/* --- GROUP 2: CHECKBOX & ERRORS --- */}
            <div className="flex flex-col gap-2">
              {/* Agree to Terms */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formik.values.agreeToTerms}
                  onChange={formik.handleChange}
                  className="mt-1 accent-blue-500"
                />
                <p className="text-sm text-gray-700">
                  By checking this box, you agree to the{" "}
                  <a
                    href="/privacy-policy.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    privacy policy</a> and{" "}
                  <a
                    href="/term-and-condition.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    terms of service</a>
                </p>
              </div>

              {/* Errors */}
              {formik.submitCount > 0 && !formik.values.agreeToTerms && (
                <div className="text-sm text-red-600">
                  You have to accept our privacy policy and terms of service
                </div>
              )}
              {forbidden && (
                <div className="text-sm text-red-600">
                  Looks like you already have an account. Please log in.
                </div>
              )}
              {error && (
                <div className="text-sm text-red-600">{error}</div>
              )}
            </div>

            {/* --- GROUP 3: ACTION BUTTONS (Signup + Divider + Google) --- */}
            {/* This div wraps the buttons together */}
            <div className="w-full flex flex-col gap-4">
              {/* Submit Button */}
              <button
                type="submit"
                className={`w-full button-1 flex items-center justify-center ${formik.values.agreeToTerms && !loading
                  ? "bg-gray-800 hover:bg-gray-700"
                  : "!bg-gray-400 !cursor-not-allowed"
                  }`}
                disabled={!formik.values.agreeToTerms || loading}
              >
                {loading ? "Signing Up..." : "Sign Up"}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-4">
                <div className="h-px bg-gray-300 flex-1" />
                <span className="text-gray-500 text-sm">or</span>
                <div className="h-px bg-gray-300 flex-1" />
              </div>

              {/* Google Button */}
              <button
                type="button"
                onClick={() => handleGoogleLogin()}
                className="button-1 !bg-white !text-gray-800 w-full border border-gray-800 flex items-center justify-center gap-2"
              >
                <img src={googleLogo} alt="Google" className="w-5 h-5" />
                Continue with Google
              </button>
            </div>

            {/* --- GROUP 4: FOOTER LINK --- */}
            {/* Login Link */}
            <div className="text-center text-sm text-gray-600">
              Already have an account?{" "}
              <span
                className="text-blue-600 underline cursor-pointer"
                onClick={() => navigate("/")}
              >
                Log in
              </span>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

export default Signup;
